import * as Yup from 'yup';
import { getFieldName } from './fieldNameMapper';
import { VALID_STATE_VALUES } from './usStates';

// Display as "Zip Code" even when the API says "Postal Code", keeping the
// original casing so placeholders like "enter your postal code" still read right
const toZipCodeWording = (text) =>
  typeof text === 'string'
    ? text.replace(/postal\s*code/gi, (m) =>
        m === m.toLowerCase() ? 'zip code' : m === m.toUpperCase() ? 'ZIP CODE' : 'Zip Code')
    : text;

// ✅ Pain level field names that should be validated as a GROUP
const PAIN_LEVEL_FIELD_NAMES = [
  'currentpainlevel',
  'bestpainlevel',
  'worstpainlevel',
  'current_pain_level',
  'best_pain_level',
  'worst_pain_level',
  'currentPainLevel',
  'bestPainLevel',
  'worstPainLevel'
];

// ✅ Helper to check if a field is a pain level field
const isPainLevelField = (fieldName) => {
  const normalizedName = fieldName?.toLowerCase()?.replace(/[_\s]/g, '') || '';
  return PAIN_LEVEL_FIELD_NAMES.some(name =>
    normalizedName === name.toLowerCase().replace(/[_\s]/g, '') ||
    normalizedName.includes('painlevel')
  );
};

// ✅ Helper to check if ANY pain level field has a valid value
const hasAnyPainLevelValue = (values) => {
  if (!values) return false;

  for (const key of Object.keys(values)) {
    if (isPainLevelField(key)) {
      const value = values[key];
      // Check if value exists and is not empty/undefined
      if (value !== undefined && value !== null && value !== '') {
        // For numbers, any value including 0 is valid IF user has interacted
        if (typeof value === 'number') return true;
        if (typeof value === 'string' && value.trim() !== '') return true;
      }
    }
  }
  return false;
};

export const generateYupSchema = (fields) => {
  if (!Array.isArray(fields) || fields.length === 0) {
    return Yup.object({});
  }

  const schemaFields = {};

  // ✅ First pass: collect all pain level field names in this form
  const painLevelFieldsInForm = [];
  fields.forEach((field, index) => {
    const field_name = getFieldName(field, index);
    if (isPainLevelField(field_name) ||
        field.label?.toLowerCase()?.includes('pain level') ||
        field.labelName?.toLowerCase()?.includes('pain level')) {
      painLevelFieldsInForm.push(field_name);
    }
  });

  fields.forEach((field, index) => {
    const {
      labelName: field_label,
      label: field_label_alt,
      required: is_required,
      type: field_type,
      options: field_options,
      termsText
    } = field;

    const finalFieldLabel = toZipCodeWording(field_label || field_label_alt);
    const field_name = getFieldName(field, index);

    // ✅ SPECIAL CASE: authorized_contacts - OPTIONAL section with conditional validation
    // If ALL fields are empty → allow user to proceed (skip this section)
    // If ANY field has data → validate fields marked as required in admin config
    if (field_type === 'authorized_contacts') {
      // Get admin configuration for contact fields
      const contactFields = field.contactFields || {};
      const roleChips = field.roleChips || {};

      // Helper to check if a field is required from admin config
      const isFieldRequired = (fieldConfig) => {
        if (!fieldConfig) return false;
        return fieldConfig.required === true || fieldConfig.required === 'true' || fieldConfig.required === 1;
      };

      // Check if contact type selection is required
      const isContactTypeRequired = roleChips.required === true || roleChips.required === 'true' || roleChips.required === 1;

      schemaFields[field_name] = Yup.mixed().test(
        'contacts-validation',
        'Please complete all required contact fields',
        function(contacts) {
          if (!contacts || !Array.isArray(contacts)) return true;

          const errors = [];

          contacts.forEach((contact, idx) => {
            if (!contact) return;

            // Helper function to check if a contact has ANY data entered
            const hasAnyData = () => {
              return (
                (contact.firstName && contact.firstName.trim() !== '') ||
                (contact.lastName && contact.lastName.trim() !== '') ||
                (contact.email && contact.email.trim() !== '') ||
                (contact.phone && contact.phone.trim() !== '') ||
                (contact.relationship && contact.relationship.trim() !== '') ||
                (contact.otherRelationship && contact.otherRelationship.trim() !== '') ||
                contact.isEmergencyContact === true ||
                contact.isAuthorizedMedical === true
              );
            };

            // ✅ OPTIONAL SECTION: If no data entered, skip validation for this contact
            if (!hasAnyData()) {
              return; // Allow empty contacts - user can skip the section
            }

            // ✅ CONDITIONAL VALIDATION: If ANY data entered, validate fields marked required in admin

            // Validate First Name (only if admin marked as required)
            if (isFieldRequired(contactFields.firstName)) {
              if (!contact.firstName || contact.firstName.trim() === '') {
                errors.push(`Contact ${idx + 1}: First Name is required`);
              }
            }

            // Validate Last Name (only if admin marked as required)
            if (isFieldRequired(contactFields.lastName)) {
              if (!contact.lastName || contact.lastName.trim() === '') {
                errors.push(`Contact ${idx + 1}: Last Name is required`);
              }
            }

            // Validate Email (only if admin marked as required)
            if (isFieldRequired(contactFields.email)) {
              if (!contact.email || contact.email.trim() === '') {
                errors.push(`Contact ${idx + 1}: Email is required`);
              } else {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(contact.email.trim())) {
                  errors.push(`Contact ${idx + 1}: Please enter a valid email`);
                }
              }
            }

            // Validate Phone (only if admin marked as required)
            if (isFieldRequired(contactFields.phone)) {
              if (!contact.phone || contact.phone.trim() === '') {
                errors.push(`Contact ${idx + 1}: Phone is required`);
              } else if (contact.phone.length !== 10) {
                errors.push(`Contact ${idx + 1}: Phone must be exactly 10 digits`);
              }
            }

            // Validate Relationship (only if admin marked as required)
            if (isFieldRequired(contactFields.relationship)) {
              if (!contact.relationship || contact.relationship.trim() === '') {
                errors.push(`Contact ${idx + 1}: Relationship is required`);
              }
            }

            // Validate Other Relationship if selected and admin marked as required
            if (contact.relationship === 'Other' && isFieldRequired(contactFields.otherRelationship)) {
              if (!contact.otherRelationship || contact.otherRelationship.trim() === '') {
                errors.push(`Contact ${idx + 1}: Please specify the relationship`);
              }
            }

            // Validate at least one contact type is selected (only if admin marked as required)
            if (isContactTypeRequired) {
              if (!contact.isEmergencyContact && !contact.isAuthorizedMedical) {
                errors.push(`Contact ${idx + 1}: Select Emergency Contact or Authorized Medical Info`);
              }
            }
          });

          if (errors.length > 0) {
            return this.createError({ message: errors.join('\n') });
          }

          return true;
        }
      );
      return; // Skip the rest of the processing for this field
    }

    // ✅ FIX: Use truthy check (same as component) - handles all possible values
    // Component uses: is_required && <span>*</span>
    // So we should also use truthy check, not strict equality
    const isRequired = !!is_required && is_required !== 'false' && is_required !== '0' && is_required !== 'no';

    if (!isRequired) return; // Skip non-required fields

    const fieldLabel = finalFieldLabel || field_name;
    let fieldSchema;

    // ✅ Check if field has options (for group-type fields like radio, checkbox, select)
    const hasOptions = field_options && Array.isArray(field_options) && field_options.length > 0;

    // ✅ Check if this is a pain level field (for GROUP validation)
    const isThisPainLevelField = isPainLevelField(field_name) ||
                                  finalFieldLabel?.toLowerCase()?.includes('pain level');

    switch (field_type) {
      case 'email':
        fieldSchema = Yup.string()
          .trim()
          .email('Please enter a valid email address')
          .required(`${fieldLabel} is required`);
        break;

      case 'number':
        fieldSchema = Yup.number()
          .typeError(`${fieldLabel} must be a number`)
          .required(`${fieldLabel} is required`);
        break;

    case 'phone':
  fieldSchema = Yup.string()
    .matches(/^[0-9]+$/, `${fieldLabel} must contain only digits`)
    .length(10, `${fieldLabel} must be exactly 10 digits`)
    .required(`${fieldLabel} is required`);
  break;



      case 'date':
        fieldSchema = Yup.date()
          .required(`${fieldLabel} is required`);
        break;

      case 'checkbox':
        // ✅ T&C checkbox (boolean)
        if ((field_name === 'tc' || fieldLabel === 'T & C') && termsText) {
          fieldSchema = Yup.boolean()
            .oneOf([true], 'You must accept the terms and conditions')
            .required('You must accept the terms and conditions');
        }
        // ✅ Multiple checkbox with options (array) - GROUP VALIDATION
        // User needs to select AT LEAST ONE option from the group
        else if (hasOptions) {
          fieldSchema = Yup.mixed()
            .test(
              'checkbox-group-required',
              `Please select at least one option for ${fieldLabel}`,
              (value) => {
                // Handle array values
                if (Array.isArray(value)) {
                  return value.length > 0;
                }
                // Handle single value (in case it's stored as string)
                if (typeof value === 'string') {
                  return value.trim() !== '';
                }
                // Handle boolean true
                if (value === true) {
                  return true;
                }
                return false;
              }
            );
        }
        // ✅ Single checkbox (boolean)
        else {
          fieldSchema = Yup.boolean()
            .oneOf([true], `${fieldLabel} must be checked`)
            .required(`${fieldLabel} must be checked`);
        }
        break;

      case 'radio':
        // ✅ Radio group validation - treats ALL options as a SINGLE required group
        // User needs to select EXACTLY ONE option - validation passes when ANY option is selected
        fieldSchema = Yup.mixed()
          .test(
            'radio-required',
            `Please select one option for ${fieldLabel}`,
            (value) => {
              // Handle string value (most common for radio)
              if (typeof value === 'string') {
                return value.trim() !== '';
              }
              // Handle number value
              if (typeof value === 'number') {
                return true; // Any number (including 0) is valid
              }
              // Handle array value (edge case)
              if (Array.isArray(value)) {
                return value.length > 0;
              }
              // Reject undefined, null, empty
              return value !== undefined && value !== null && value !== '';
            }
          );
        break;

      case 'select':
      case 'multiselect':
        // ✅ Select/Dropdown validation - handles both single and multi-select
        // For group selection (like case range with 3 options), selecting ANY ONE is valid
        fieldSchema = Yup.mixed()
          .test(
            'select-required',
            `Please select an option for ${fieldLabel}`,
            (value) => {
              // Handle array (multi-select)
              if (Array.isArray(value)) {
                return value.length > 0;
              }
              // Handle string (single select)
              if (typeof value === 'string') {
                return value.trim() !== '';
              }
              // Handle number value
              if (typeof value === 'number') {
                return true;
              }
              // Handle object value
              if (typeof value === 'object' && value !== null) {
                return true;
              }
              return false;
            }
          );
        break;

      case 'range':
        // ✅ Range/Slider validation with GROUP support for pain levels
        // If this is a pain level field, validate as a GROUP (any one selected = all pass)
        if (isThisPainLevelField && painLevelFieldsInForm.length > 1) {
          // GROUP VALIDATION: If ANY pain level field has value, this field passes
          fieldSchema = Yup.mixed()
            .test(
              'pain-level-group-required',
              `Please select a value for ${fieldLabel}`,
              function(value) {
                // Get all form values from Yup context
                const allValues = this.parent;

                // Check if THIS field has a value
                if (typeof value === 'number') return true;
                if (typeof value === 'string' && value.trim() !== '') return true;

                // Check if ANY OTHER pain level field has a value
                for (const painFieldName of painLevelFieldsInForm) {
                  const painValue = allValues[painFieldName];
                  if (painValue !== undefined && painValue !== null && painValue !== '') {
                    if (typeof painValue === 'number') return true;
                    if (typeof painValue === 'string' && painValue.trim() !== '') return true;
                  }
                }

                return false;
              }
            );
        } else {
          // Regular range validation (non-pain-level fields)
          fieldSchema = Yup.mixed()
            .test(
              'range-required',
              `Please select a value for ${fieldLabel}`,
              (value) => {
                if (typeof value === 'number') return true;
                if (typeof value === 'string' && value.trim() !== '') return true;
                return false;
              }
            );
        }
        break;

      case 'file':
      case 'image':
        fieldSchema = Yup.mixed()
          .required(`${fieldLabel} is required`);
        break;

      case 'address':
        // ✅ Composite Address field - validate each subfield based on addressConfig
        const addressConfig = field.addressConfig || {
          streetAddress: { label: 'Street Address', required: true, enabled: true },
          suiteApt: { label: 'Suite No/Apt No.', required: false, enabled: true },
          city: { label: 'City', required: true, enabled: true },
          state: { label: 'State', required: true, enabled: true },
          country: { label: 'Country', required: false, enabled: true }
        };

        // Street Address (only validate if enabled)
        if (addressConfig.streetAddress?.enabled !== false && addressConfig.streetAddress?.required) {
          schemaFields[`${field_name}_street`] = Yup.string()
            .trim()
            .required(`${addressConfig.streetAddress?.label || 'Street Address'} is required`);
        }

        // Suite/Apt (only validate if enabled and required)
        if (addressConfig.suiteApt?.enabled !== false && addressConfig.suiteApt?.required) {
          schemaFields[`${field_name}_suite`] = Yup.string()
            .trim()
            .required(`${addressConfig.suiteApt?.label || 'Suite No/Apt No.'} is required`);
        }

        // City (only validate if enabled)
        if (addressConfig.city?.enabled !== false && addressConfig.city?.required) {
          schemaFields[`${field_name}_city`] = Yup.string()
            .trim()
            .required(`${addressConfig.city?.label || 'City'} is required`);
        }

        // State (only validate if enabled) - must be valid US state
        if (addressConfig.state?.enabled !== false && addressConfig.state?.required) {
          schemaFields[`${field_name}_state`] = Yup.string()
            .required(`${addressConfig.state?.label || 'State'} is required`)
            .oneOf(VALID_STATE_VALUES, 'Please select a valid US state');
        }

        // Country is always US and disabled, no validation needed
        return; // Skip the default fieldSchema assignment

      case 'slider':
        // ✅ Slider type with GROUP support for pain levels (same as range)
        if (isThisPainLevelField && painLevelFieldsInForm.length > 1) {
          fieldSchema = Yup.mixed()
            .test(
              'pain-level-group-required',
              `Please select a value for ${fieldLabel}`,
              function(value) {
                const allValues = this.parent;
                if (typeof value === 'number') return true;
                if (typeof value === 'string' && value.trim() !== '') return true;

                for (const painFieldName of painLevelFieldsInForm) {
                  const painValue = allValues[painFieldName];
                  if (painValue !== undefined && painValue !== null && painValue !== '') {
                    if (typeof painValue === 'number') return true;
                    if (typeof painValue === 'string' && painValue.trim() !== '') return true;
                  }
                }
                return false;
              }
            );
        } else {
          fieldSchema = Yup.mixed()
            .test(
              'slider-required',
              `Please select a value for ${fieldLabel}`,
              (value) => {
                if (typeof value === 'number') return true;
                if (typeof value === 'string' && value.trim() !== '') return true;
                return false;
              }
            );
        }
        break;

      default:
        // ✅ FIX: If field has options but type is unknown, treat it as a group selection
        // This ensures that ANY selection from the group satisfies validation
        if (hasOptions) {
          fieldSchema = Yup.mixed()
            .test(
              'options-group-required',
              `Please select an option for ${fieldLabel}`,
              (value) => {
                if (Array.isArray(value)) {
                  return value.length > 0;
                }
                if (typeof value === 'string') {
                  return value.trim() !== '';
                }
                if (typeof value === 'number') {
                  return true;
                }
                return value !== undefined && value !== null;
              }
            );
        } else {
          fieldSchema = Yup.string()
            .trim()
            .required(`${fieldLabel} is required`);
        }
    }

    // ✅ Assign only once
    schemaFields[field_name] = fieldSchema;
  });

  return Yup.object().shape(schemaFields);
};
